import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type { AdminPulseDayResponse } from '@/lib/types';

function formatEventLabel(eventType: string) {
  return eventType
    .split('.')
    .map((part) =>
      part
        .split('_')
        .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
        .join(' '),
    )
    .join(' · ');
}

/** Tailwind classes: bg, text, ring (subtle border via ring-1) */
const PULSE_EVENT_TYPE_CHIP: Record<string, string> = {
  // Exact matches (extend freely)
  'message.created': 'bg-sky-100 text-sky-900 ring-1 ring-sky-200/90',
  'message.updated': 'bg-sky-100 text-sky-800 ring-1 ring-sky-200/90',
  'message.deleted': 'bg-rose-100 text-rose-900 ring-1 ring-rose-200/90',
  'payment.created': 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200/90',
  'payment.updated': 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200/90',
  'payment.deleted': 'bg-rose-100 text-rose-900 ring-1 ring-rose-200/90',
  'exchange_log.created': 'bg-violet-100 text-violet-900 ring-1 ring-violet-200/90',
  'expense.created': 'bg-amber-100 text-amber-950 ring-1 ring-amber-200/90',
  'expense.updated': 'bg-amber-100 text-amber-900 ring-1 ring-amber-200/90',
  'expense.deleted': 'bg-orange-100 text-orange-900 ring-1 ring-orange-200/90',
};

/** First segment of `entity.action` → default chip (non-delete actions) */
const PULSE_ENTITY_PREFIX_CHIP: Record<string, string> = {
  message: 'bg-sky-100 text-sky-900 ring-1 ring-sky-200/90',
  payment: 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200/90',
  exchange_log: 'bg-violet-100 text-violet-900 ring-1 ring-violet-200/90',
  expense: 'bg-amber-100 text-amber-950 ring-1 ring-amber-200/90',
  journal: 'bg-teal-100 text-teal-900 ring-1 ring-teal-200/90',
  household: 'bg-indigo-100 text-indigo-900 ring-1 ring-indigo-200/90',
  user: 'bg-slate-200 text-slate-900 ring-1 ring-slate-300/90',
  check_in: 'bg-cyan-100 text-cyan-900 ring-1 ring-cyan-200/90',
  professional_invite: 'bg-fuchsia-100 text-fuchsia-900 ring-1 ring-fuchsia-200/90',
};

const DELETE_LIKE = new Set(['deleted', 'destroyed', 'removed']);

function pulseEventChipClass(eventType: string): string {
  const exact = PULSE_EVENT_TYPE_CHIP[eventType];
  if (exact) return exact;
  const dot = eventType.indexOf('.');
  if (dot === -1) {
    return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200/90';
  }
  const entity = eventType.slice(0, dot);
  const action = eventType.slice(dot + 1);
  if (DELETE_LIKE.has(action)) {
    return 'bg-rose-100 text-rose-900 ring-1 ring-rose-200/90';
  }
  return (
    PULSE_ENTITY_PREFIX_CHIP[entity] ?? 'bg-slate-100 text-slate-800 ring-1 ring-slate-200/90'
  );
}

function PulseEventTypeChip({ eventType }: { eventType: string }) {
  return (
    <span
      className={`inline-flex max-w-full items-center rounded-full px-2.5 py-0.5 text-xs font-semibold leading-tight ${pulseEventChipClass(eventType)}`}
      title={eventType}
    >
      <span className='truncate'>{formatEventLabel(eventType)}</span>
    </span>
  );
}

/** IANA zone for Pulse: US Pacific (PST / PDT). */
const PULSE_TIME_ZONE = 'America/Los_Angeles';

function parseYmd(s: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const u = new Date(Date.UTC(y, m - 1, d));
  if (u.getUTCFullYear() !== y || u.getUTCMonth() !== m - 1 || u.getUTCDate() !== d) return null;
  return s;
}

/** Civil calendar step on the Y-M-D string (same as Pacific wall date when interpreted server-side). */
function addDaysYmd(ymd: string, delta: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const u = new Date(Date.UTC(y, m - 1, d + delta));
  return `${u.getUTCFullYear()}-${String(u.getUTCMonth() + 1).padStart(2, '0')}-${String(u.getUTCDate()).padStart(2, '0')}`;
}

function todayPacificYmd(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: PULSE_TIME_ZONE });
}

function formatPacificTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    timeZone: PULSE_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Same destination and styling as the Users table “View” action. */
const ADMIN_USER_VIEW_CLASS =
  'inline-flex px-2 py-1 rounded-md border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-100';

const statCards: { key: keyof AdminPulseDayResponse['summary']; label: string; hint?: string }[] = [
  { key: 'users_registered', label: 'New users' },
  { key: 'households_created', label: 'Households' },
  { key: 'professional_invites', label: 'Pro invites' },
  { key: 'messages_created', label: 'Chat messages', hint: 'Main chat only (no dispute threads)' },
  { key: 'expenses_created', label: 'Expenses' },
  { key: 'payments_created', label: 'Payments' },
  { key: 'journal_entries_created', label: 'Journal' },
  { key: 'check_ins_created', label: 'Check-ins' },
  { key: 'audit_events', label: 'Audit events' },
];

function bucketKey(actorUserId: number | null): string {
  return actorUserId != null ? String(actorUserId) : 'system';
}

function AdminPulsePage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { date: dateParam } = Route.useSearch();
  /** Buckets whose event list is hidden; default is expanded for everyone. */
  const [collapsedBucketKeys, setCollapsedBucketKeys] = useState<Set<string>>(() => new Set());

  const dateStr = useMemo(() => {
    const parsed = dateParam ? parseYmd(dateParam) : null;
    return parsed ?? todayPacificYmd();
  }, [dateParam]);

  const pulseQuery = useQuery({
    queryKey: ['admin', 'pulse', dateStr],
    queryFn: () =>
      apiJson<AdminPulseDayResponse>(getToken, `/v1/admin/pulse?date=${encodeURIComponent(dateStr)}`),
  });

  const setDate = useCallback(
    (next: string) => {
      navigate({ to: '/admin/pulse', search: { date: next } });
    },
    [navigate],
  );

  const toggleBucketCollapsed = useCallback((key: string) => {
    setCollapsedBucketKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const data = pulseQuery.data;

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-900'>Pulse</h1>
          <p className='text-sm text-gray-500 mt-1'>
            Daily activity and audit trail. Each page is one calendar day in US Pacific (Los Angeles),
            and timestamps are shown in that timezone.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <button
            type='button'
            onClick={() => setDate(addDaysYmd(dateStr, -1))}
            className='p-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
            aria-label='Previous day'
          >
            <FiChevronLeft className='w-5 h-5' />
          </button>
          <input
            type='date'
            value={dateStr}
            onChange={(e) => {
              const v = e.target.value;
              if (v) setDate(v);
            }}
            className='px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900'
          />
          <button
            type='button'
            onClick={() => setDate(addDaysYmd(dateStr, 1))}
            className='p-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
            aria-label='Next day'
          >
            <FiChevronRight className='w-5 h-5' />
          </button>
          <button
            type='button'
            onClick={() => setDate(todayPacificYmd())}
            className='px-3 py-2 text-sm font-semibold rounded-md border border-primary text-primary hover:bg-primary/5'
          >
            Today (Pacific)
          </button>
        </div>
      </div>

      {pulseQuery.isLoading ? (
        <div className='flex justify-center py-16'>
          <div className='animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full' />
        </div>
      ) : pulseQuery.isError ? (
        <p className='text-red-600'>Could not load pulse for this day.</p>
      ) : !data ? null : (
        <>
          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-2'>
            {statCards.map(({ key, label, hint }) => (
              <div
                key={key}
                title={hint}
                className='bg-white rounded-lg border border-gray-200 px-3 py-3 shadow-sm'
              >
                <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight'>
                  {label}
                </p>
                <p className='mt-1 text-xl font-semibold tabular-nums text-gray-900'>
                  {data.summary[key].toLocaleString()}
                </p>
              </div>
            ))}
          </div>

          <div className='grid grid-cols-1 xl:grid-cols-3 gap-6'>
            <section className='xl:col-span-2 space-y-3'>
              <h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>
                Audit by user
              </h2>
              <div className='space-y-4'>
                {data.audit_by_user.length === 0 ? (
                  <div className='bg-white rounded-lg border border-gray-200 shadow-sm p-6'>
                    <p className='text-sm text-gray-500'>No audit events this day.</p>
                  </div>
                ) : (
                  data.audit_by_user.map((bucket) => {
                    const bKey = bucketKey(bucket.actor_user_id);
                    const collapsed = collapsedBucketKeys.has(bKey);
                    const listId = `pulse-audit-events-${bKey}`;
                    return (
                      <div
                        key={bKey}
                        className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'
                      >
                        <div className='px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-start justify-between gap-3'>
                          <button
                            type='button'
                            id={`pulse-audit-header-${bKey}`}
                            aria-expanded={!collapsed}
                            aria-controls={listId}
                            onClick={() => toggleBucketCollapsed(bKey)}
                            className='min-w-0 flex-1 text-left cursor-pointer rounded-md -m-1 p-1 hover:bg-gray-100/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 flex items-start gap-2'
                          >
                            <span className='shrink-0 mt-0.5 text-gray-500' aria-hidden>
                              {collapsed ? (
                                <FiChevronRight className='w-4 h-4' />
                              ) : (
                                <FiChevronDown className='w-4 h-4' />
                              )}
                            </span>
                            <span className='min-w-0 flex-1'>
                              <p className='font-semibold text-gray-900 truncate'>
                                {bucket.display_name ?? '—'}
                                {bucket.actor_user_id != null ? (
                                  <span className='ml-2 text-xs font-mono font-normal text-gray-500'>
                                    #{bucket.actor_user_id}
                                  </span>
                                ) : null}
                              </p>
                              <p className='text-xs text-gray-500 mt-0.5 truncate'>
                                {bucket.actor_user_id != null
                                  ? [bucket.email, bucket.phone_number].filter(Boolean).join(' · ') || '—'
                                  : 'Jobs, admin actions, and other events with no user actor'}
                              </p>
                            </span>
                            <span className='text-xs font-semibold tabular-nums text-gray-600 bg-white border border-gray-200 rounded-full px-2.5 py-0.5 shrink-0 self-center'>
                              {bucket.event_count} event{bucket.event_count === 1 ? '' : 's'}
                            </span>
                          </button>
                          {bucket.actor_user_id != null ? (
                            <Link
                              to='/admin/users/$userId'
                              params={{ userId: String(bucket.actor_user_id) }}
                              search={{ hid: undefined }}
                              className={`shrink-0 self-center ${ADMIN_USER_VIEW_CLASS}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              View
                            </Link>
                          ) : null}
                        </div>
                        {!collapsed ? (
                          <ul id={listId} className='divide-y divide-gray-100' role='region' aria-labelledby={`pulse-audit-header-${bKey}`}>
                            {bucket.events.map((ev) => (
                              <li key={ev.id} className='px-4 py-2.5 hover:bg-gray-50/80 flex gap-3 text-sm'>
                                <time
                                  className='shrink-0 w-[6.75rem] text-xs text-gray-500 tabular-nums pt-0.5'
                                  dateTime={ev.created_at}
                                >
                                  {formatPacificTime(ev.created_at)}
                                </time>
                                <div className='min-w-0 flex-1'>
                                  <p className='leading-snug'>
                                    <PulseEventTypeChip eventType={ev.event_type} />
                                  </p>
                                  <p className='text-xs text-gray-600 mt-0.5'>
                                    <span className='font-mono text-gray-700'>{ev.entity_type}</span>
                                    <span className='text-gray-400'> #{ev.entity_id}</span>
                                    {ev.household_id != null ? (
                                      <span className='text-gray-400'> · HH {ev.household_id}</span>
                                    ) : null}
                                  </p>
                                  {ev.detail ? (
                                    <p className='text-xs text-gray-500 mt-1 line-clamp-3 break-words'>{ev.detail}</p>
                                  ) : null}
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
              <p className='text-xs text-gray-400'>
                Up to 1,000 audit rows for the Pacific day, grouped by actor. Newest rows fetched first;
                within each person, events stay in that order.
              </p>
            </section>

            <div className='space-y-6'>
              <section className='space-y-3'>
                <h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>
                  New users
                </h2>
                <div className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
                  {data.new_users.length === 0 ? (
                    <p className='p-4 text-sm text-gray-500'>None this day.</p>
                  ) : (
                    <ul className='divide-y divide-gray-100 max-h-64 overflow-y-auto'>
                      {data.new_users.map((u) => (
                        <li key={u.id} className='px-4 py-3 flex items-start justify-between gap-2'>
                          <div className='min-w-0'>
                            <p className='font-medium text-gray-900 truncate'>
                              {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                            </p>
                            <p className='text-xs text-gray-500 truncate'>{u.email ?? u.phone_number ?? '—'}</p>
                            <p className='text-[10px] text-gray-400 mt-1 tabular-nums'>
                              {formatPacificTime(u.created_at)}
                            </p>
                          </div>
                          <Link
                            to='/admin/users/$userId'
                            params={{ userId: String(u.id) }}
                            search={{ hid: undefined }}
                            className={`shrink-0 ${ADMIN_USER_VIEW_CLASS}`}
                          >
                            View
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              <section className='space-y-3'>
                <h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>
                  Audit by event type
                </h2>
                <div className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
                  {data.audit_by_event_type.length === 0 ? (
                    <p className='p-4 text-sm text-gray-500'>No data.</p>
                  ) : (
                    <ul className='divide-y divide-gray-100 max-h-80 overflow-y-auto'>
                      {data.audit_by_event_type.map((row) => (
                        <li
                          key={row.event_type}
                          className='px-4 py-2 flex items-center justify-between gap-2 text-sm'
                        >
                            <span className='min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1'>
                              <PulseEventTypeChip eventType={row.event_type} />
                              <span className='font-mono text-[10px] text-gray-400' title={row.event_type}>
                                {row.event_type}
                              </span>
                            </span>
                          <span className='tabular-nums font-semibold text-gray-700 shrink-0'>
                            {row.count.toLocaleString()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/pulse')({
  validateSearch: (search: Record<string, unknown>) => ({
    date: typeof search.date === 'string' ? search.date : undefined,
  }),
  component: AdminPulsePage,
});
